import { ReactNode } from "react";

import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";

import { PortalAccountStateDto } from "@/api";
import { useTranslation } from "@/translations";

import { PortalAccountStatusCard } from "./components/PortalAccountStatusCard";
import { PortalManageAccessCard } from "./components/PortalManageAccessCard";
import { PortalPendingInvitationCard } from "./components/PortalPendingInvitationCard";
import { PortalAccessTexts, PortalAccountController } from "./portalAccess.types";

interface Props {
  /** The result of usePortalAccount — the entry calls the hook so it can also feed its invitation card. */
  account: PortalAccountController;
  texts: PortalAccessTexts;
  /** Rendered in the NotGranted state; the subject decides how the address is chosen. */
  invitationCard: ReactNode;
  /** Only clients record ID-document details (Ausweisdaten), so only their page shows the rows. */
  showIdentityDocument?: boolean;
}

/**
 * The shared portal-access page body (Kund:innen-Zugang / Zugang Ehrenamtliche): header, current
 * status, and the state-dependent action card. Subject-specific sentences come in as texts, the
 * subject-specific initial-activation card as a slot, and optional capabilities follow the
 * controller's optional handlers.
 */
export function PortalAccessView({ account, texts, invitationCard, showIdentityDocument = false }: Props) {
  const { t } = useTranslation();
  const { status, isPending, isError, isLocked, isBusy } = account;

  const renderStatus = () => {
    if (isPending) {
      return (
        <Stack alignItems="center" py={4}>
          <CircularProgress aria-label={t("portalAccess.loading")} />
        </Stack>
      );
    }

    // A failed query settles with isPending false and no data — without this branch the loading
    // spinner would render forever.
    if (isError || !status) {
      return (
        <Alert severity="error" variant="outlined">
          {t("portalAccess.loadError")}
        </Alert>
      );
    }

    return <PortalAccountStatusCard status={status} showIdentityDocument={showIdentityDocument} />;
  };

  const renderActionCard = () => {
    if (!status) {
      return null;
    }

    switch (status.state) {
      case PortalAccountStateDto.NotGranted:
        return invitationCard;
      case PortalAccountStateDto.InvitationSent:
        return (
          <PortalPendingInvitationCard
            status={status}
            description={texts.pendingDescription}
            onResend={account.onResend}
            onWithdraw={account.onWithdraw}
            isBusy={isBusy}
          />
        );
      case PortalAccountStateDto.Registered:
      case PortalAccountStateDto.Locked:
        return (
          <PortalManageAccessCard
            isLocked={isLocked}
            onLock={account.onLock}
            onUnlock={account.onUnlock}
            onSendPasswordReset={account.onSendPasswordReset}
            onRemoveUser={account.onRemoveUser}
            isBusy={isBusy}
          />
        );
    }
  };

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      <Stack gap={2} bgcolor="background.paper" p={2} flexShrink={0}>
        <Box>
          <Typography variant="h5">{texts.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {texts.description}
          </Typography>
        </Box>

        {renderStatus()}
      </Stack>

      {renderActionCard()}
    </Stack>
  );
}
